import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeColors } from '../../utils/theme';
import API_ENDPOINTS from '../../config';
import logger from '../../utils/logger';
import { authenticatedFetch } from '../../utils/api';

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/dashboard`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard stats');
      }

      const data = await response.json();
      setStats(data);
      setRecent(data.recent);
    } catch (error) {
      logger.error('Error fetching dashboard stats:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        setError(t('admin.dashboard.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>{t('admin.dashboard.loadingStats')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
        <div style={{ fontSize: '18px', color: colors.error, transition: 'color 0.3s ease' }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, margin: '0 0 0.5rem 0', transition: 'color 0.3s ease' }}>
          {t('admin.dashboard.title')}
        </h1>
        <p style={{ color: colors.textSecondary, margin: 0, transition: 'color 0.3s ease' }}>
          {t('admin.dashboard.subtitle')}
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats?.totalUsers || 0}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>{t('admin.dashboard.totalUsers')}</div>
            </div>
            <div style={{ fontSize: '2rem' }}>👥</div>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(240, 147, 251, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats?.totalCompanies || 0}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>{t('admin.dashboard.totalCompanies')}</div>
            </div>
            <div style={{ fontSize: '2rem' }}>🏢</div>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats?.totalUploads || 0}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>{t('admin.dashboard.totalFiles')}</div>
            </div>
            <div style={{ fontSize: '2rem' }}>📁</div>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(67, 233, 123, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{formatBytes(stats?.totalStorage || 0)}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>{t('admin.dashboard.totalStorage')}</div>
            </div>
            <div style={{ fontSize: '2rem' }}>💾</div>
          </div>
        </div>
      </div>

      {/* Trial and Subscription Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{
          background: colors.card,
          padding: '1rem',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center',
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.warning, transition: 'color 0.3s ease' }}>{stats?.activeTrials || 0}</div>
          <div style={{ fontSize: '14px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>{t('admin.dashboard.activeTrials')}</div>
        </div>

        <div style={{
          background: colors.card,
          padding: '1rem',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          textAlign: 'center',
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.success, transition: 'color 0.3s ease' }}>{stats?.paidCompanies || 0}</div>
          <div style={{ fontSize: '14px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>{t('admin.dashboard.paidSubscriptions')}</div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
        {/* Recent Uploads */}
        <div style={{
          background: colors.card,
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: `0 4px 20px ${colors.shadowHover}`,
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>
            📁 {t('admin.dashboard.recentFiles')}
          </h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {recent?.uploads?.length > 0 ? (
              recent.uploads.map((upload, index) => (
                <div key={upload.id} style={{
                  padding: '0.75rem',
                  borderBottom: index < recent.uploads.length - 1 ? `1px solid ${colors.border}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'border-color 0.3s ease',
                }}>
                  <div style={{ fontSize: '20px' }}>📄</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>{upload.originalName}</div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
                      {upload.User?.firstName} {upload.User?.lastName} • {upload.Company?.name}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
                    {formatDate(upload.createdAt)}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '1rem', transition: 'color 0.3s ease' }}>
                {t('admin.dashboard.noRecentFiles')}
              </div>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div style={{
          background: colors.card,
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: `0 4px 20px ${colors.shadowHover}`,
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>
            👥 {t('admin.dashboard.recentUsers')}
          </h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {recent?.users?.length > 0 ? (
              recent.users.map((user, index) => (
                <div key={user.id} style={{
                  padding: '0.75rem',
                  borderBottom: index < recent.users.length - 1 ? `1px solid ${colors.border}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'border-color 0.3s ease',
                }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    background: colors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'background-color 0.3s ease',
                  }}>
                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: colors.text, transition: 'color 0.3s ease' }}>
                      {user.firstName} {user.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
                      {user.email} • {user.Company?.name}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
                    {formatDate(user.createdAt)}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '1rem', transition: 'color 0.3s ease' }}>
                {t('admin.dashboard.noRecentUsers')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 